/**
 * Track persistence.
 *
 * Same database as routes (`db.ts`), same defensive reads. One difference in
 * spirit: a route that fails validation is dropped from the listing because a
 * broken plan is worse than no plan, and a track that fails validation is
 * dropped for a stronger reason — it is a claim about where a boat has been,
 * and a claim that cannot be checked should not be made.
 *
 * Saves happen while recording, so this is written to be called often: a whole
 * track is put each time rather than appending points, which IndexedDB handles
 * comfortably at the sizes `TRACK_MAX_POINTS` allows and which keeps the stored
 * value identical to the one in memory.
 */

import { type Track, trackStats, validateTrack } from '../core/track';
import { TRACK_STORE as STORE, db, isStorageAvailable } from './db';

/** True when this browser can persist tracks at all. */
export function isTrackStorageAvailable(): boolean {
  return isStorageAvailable();
}

export async function saveTrack(track: Track): Promise<void> {
  const problems = validateTrack(track);
  if (problems.length) {
    throw new Error(`Track cannot be saved: ${problems.join('; ')}`);
  }
  await (await db()).put(STORE, track);
}

export async function deleteTrack(id: string): Promise<void> {
  await (await db()).delete(STORE, id);
}

export async function loadTrack(id: string): Promise<Track | null> {
  const track = await (await db()).get(STORE, id);
  if (!track) return null;
  return validateTrack(track).length === 0 ? track : null;
}

/**
 * All stored tracks, most recent first.
 *
 * Without their points. A season of tracks is megabytes, and the listing only
 * ever shows a name, a date and a distance — loading every position to draw a
 * six-line list is what makes a panel take a second to open on a phone. Call
 * `loadTrack` for the one being opened.
 */
export async function listTracks(): Promise<TrackSummary[]> {
  const all = await (await db()).getAllFromIndex(STORE, 'by-updated');
  return all
    .filter((track) => validateTrack(track).length === 0)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map(summarise);
}

export interface TrackSummary {
  id: string;
  name: string;
  startedAt: number;
  updatedAt: number;
  simulated: boolean;
  points: number;
  /** Distance run, metres. */
  distanceM: number;
  /** First point to last, seconds. */
  durationS: number;
}

function summarise(track: Track): TrackSummary {
  // From core, not reimplemented here: the listing and the open track must not
  // be able to disagree about how far the boat went.
  const stats = trackStats(track);
  return {
    id: track.id,
    name: track.name,
    startedAt: track.startedAt,
    updatedAt: track.updatedAt,
    simulated: track.simulated,
    points: stats.points,
    distanceM: stats.distanceM,
    durationS: stats.durationS,
  };
}

/** Drop everything. Used by tests and by an explicit reset. */
export async function clearTracks(): Promise<void> {
  await (await db()).clear(STORE);
}
